<template>
  <div class="api-key-container">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span class="card-title">开发者中心 - API Key 管理</span>
        <el-button
          style="float: right;"
          type="primary"
          icon="el-icon-plus"
          size="small"
          @click="showCreateDialog"
        >创建密钥</el-button>
      </div>

      <el-alert
        title="安全提示：请妥善保管您的 Secret Access Key (SK)。开放平台接口调用涉及您的账户权限，切勿泄露给他人。"
        type="warning"
        show-icon
        :closable="false"
        style="margin-bottom: 20px;"
      />

      <el-table v-loading="loading" :data="apiKeyList" border style="width: 100%">
        <el-table-column prop="appName" label="应用/脚本名称" min-width="150" />
        <el-table-column prop="accessKey" label="Access Key ID (AK)" min-width="180" />
        <el-table-column label="权限范围 (Scopes)" min-width="180">
          <template slot-scope="scope">
            <el-tag
              v-for="item in splitScopes(scope.row.scopes)"
              :key="item"
              size="mini"
              style="margin-right: 5px;"
            >
              {{ item }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'">
              {{ scope.row.status === 1 ? '已启用' : '已禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="160" />
        <el-table-column label="操作" width="160" align="center">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.status === 1"
              size="mini"
              type="text"
              style="color: #E6A23C;"
              @click="handleDisable(scope.row)"
            >禁用</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog title="创建开发者密钥" :visible.sync="createDialogVisible" width="500px">
      <el-form ref="createForm" :model="createForm" :rules="rules" label-width="110px">
        <el-form-item label="应用名称" prop="appName">
          <el-input v-model="createForm.appName" placeholder="例如：Python自动上传脚本" />
          <el-form-item label="权限范围" prop="scopes">
            <el-checkbox-group v-model="createForm.scopes">
              <el-checkbox label="video:write">发布视频 (video:write)</el-checkbox>
              <el-checkbox label="video:read">读取视频数据 (video:read)</el-checkbox>
            </el-checkbox-group>
          </el-form-item>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="createDialogVisible = false">取 消</el-button>
        <el-button type="primary" size="small" :loading="submitLoading" @click="submitCreate">确 定</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="🔑 密钥创建成功（请立即复制保存）"
      :visible.sync="resultDialogVisible"
      width="550px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
    >
      <el-alert
        title="以下 Secret Key 仅在创建时显示一次。如果您关闭此窗口或刷新页面，将永远无法再次查看该密钥！"
        type="error"
        :closable="false"
        style="margin-bottom: 20px;"
      />

      <el-form label-position="top">
        <el-form-item label="Access Key ID (AK)">
          <el-input v-model="generatedResult.accessKey" readonly>
            <el-button slot="append" icon="el-icon-document-copy" @click="copyText(generatedResult.accessKey)">复制</el-button>
          </el-input>
        </el-form-item>
        <el-form-item label="Secret Access Key (SK)">
          <el-input v-model="generatedResult.secretKey" readonly type="password" show-password>
            <el-button slot="append" icon="el-icon-document-copy" @click="copyText(generatedResult.secretKey)">复制</el-button>
          </el-input>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button type="danger" size="small" style="width: 100%;" @click="closeResultDialog">我已安全复制并保存密钥，关闭窗口</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getApiKeyList, createApiKey, disableApiKey } from '@/api/account'

export default {
  name: 'MyApiKey',
  data() {
    return {
      loading: false,
      submitLoading: false,
      apiKeyList: [],

      // 创建表单相关
      createDialogVisible: false,
      createForm: {
        appName: '',
        scopes: ['video:write'] // 默认勾选视频发布
      },
      rules: {
        appName: [
          { required: true, message: '请输入应用或脚本名称', trigger: 'blur' },
          { min: 2, max: 30, message: '长度在 2 到 30 个字符', trigger: 'blur' }
        ],
        scopes: [
          { type: 'array', required: true, message: '请至少选择一个权限范围', trigger: 'change' }
        ]
      },

      // 结果展示相关
      resultDialogVisible: false,
      generatedResult: {
        accessKey: '',
        secretKey: ''
      }
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    // 获取列表数据
    async fetchData() {
      this.loading = true
      try {
        const res = await getApiKeyList()
        if (res.code === 0) {
          this.apiKeyList = res.data
        }
      } catch (error) {
        this.$message.error('获取密钥列表失败')
      } finally {
        this.loading = false
      }
    },

    // 打开创建弹窗
    showCreateDialog() {
      this.createForm.appName = ''
      this.createForm.scopes = ['video:write']
      this.createDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.createForm) this.$refs.createForm.clearValidate()
      })
    },

    // 提交创建
    submitCreate() {
      this.$refs.createForm.validate(async(valid) => {
        if (!valid) return
        this.submitLoading = true
        try {
          const res = await createApiKey(this.createForm)
          if (res.code === 200) {
            this.createDialogVisible = false
            // 将后端返回的明文 AK/SK 赋予结果变量
            this.generatedResult.accessKey = res.data.accessKey
            this.generatedResult.secretKey = res.data.secretKey
            // 唤起不可盲退的“成果展示”弹窗
            this.resultDialogVisible = true
          }
        } catch (error) {
          this.$message.error('创建密钥失败')
        } finally {
          this.submitLoading = false
        }
      })
    },

    // 关闭结果弹窗，并刷新列表
    closeResultDialog() {
      this.resultDialogVisible = false
      this.generatedResult.accessKey = ''
      this.generatedResult.secretKey = ''
      this.fetchData() // 重新加载列表（此时列表中不再有SK明文）
    },

    // 禁用密钥
    handleDisable(row) {
      this.$confirm(`确定要禁用应用【${row.appName}】的 API Key 吗？禁用后相关脚本将立即失效且无法恢复。`, '安全警告', {
        confirmButtonText: '确定禁用',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async() => {
        const res = await disableApiKey(row.accessKey)
        if (res.code === 200) {
          this.$message.success('该密钥已成功禁用')
          this.fetchData()
        }
      }).catch(() => {})
    },

    // 辅助工具：切分逗号分隔的权限范围字符串
    splitScopes(scopesStr) {
      return scopesStr ? scopesStr.split(',') : []
    },

    // 辅助工具：原生浏览器文本一键复制
    copyText(text) {
      const input = document.createElement('input')
      input.value = text
      document.body.appendChild(input)
      input.select()
      try {
        document.execCommand('copy')
        this.$message({ message: '已复制到剪贴板', type: 'success', duration: 1500 })
      } catch (err) {
        this.$message.error('复制失败，请手动选择复制')
      }
      document.body.removeChild(input)
    }
  }
}
</script>

<style scoped>
.api-key-container {
  padding: 20px;
}
.card-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}
.dialog-footer {
  text-align: right;
}
</style>
